/**
 * Surveys API — in-app member micro-surveys (ClickUp #86d3pz5x4).
 *
 * Two member operations, both SECURITY DEFINER RPCs. Authorization and
 * eligibility live in the database, not here:
 *
 *   rpc_get_active_survey_for_me(p_placement) — sole owner of eligibility
 *     (status, date window, tier scope, 1-in-N sampling, show-once). Returns the
 *     one survey due, or null, and claims it by writing a survey_invites row.
 *   rpc_submit_survey_response(p_survey_id, p_answers, p_anonymous) — the only
 *     write path for an answer. Takes FOR UPDATE on the invite so
 *     double-submission is impossible, and decides anonymity server-side.
 *
 * ANONYMITY is one-way. The member may opt IN on an identified survey; they can
 * never opt OUT of one the admin marked anonymous, because the server computes
 * `is_anonymous OR p_anonymous` and ignores a false flag on an anonymous survey.
 * survey_responses has no member SELECT and no member INSERT policy — do not add
 * a direct insert path here.
 *
 * Types are declared locally rather than imported from `@hermes/db/schema`
 * because the RPCs return camelCase `jsonb_build_object` payloads, not Drizzle
 * row shapes — the wire contract is genuinely this module's concern. The
 * canonical DB definition is packages/db/src/schema/surveys.ts; keep the two in
 * step by hand. (Admin reads below go through PostgREST and so are snake_case,
 * mirroring MentorCandidate in admin-mentorship.ts.)
 */
import { EDGE_FUNCTION_INVOKE_REGION } from '@/lib/edge-functions';
import { supabase } from '@/lib/supabase';

// ─── Shared question / answer shapes ────────────────────────────────────────

export type SurveyScaleQuestion = {
  id: string;
  type: 'scale';
  label: string;
  /** Admin-selectable upper bound; the composer defaults it to 10. */
  scaleMax?: number;
  lowLabel?: string;
  highLabel?: string;
  optional?: boolean;
};

export type SurveyShortAnswerQuestion = {
  id: string;
  type: 'short_answer';
  label: string;
  placeholder?: string;
  optional?: boolean;
};

/**
 * Yes/no, optionally yes/no/maybe. The answer is the option STRING, so
 * `SurveyAnswers` below needs no change and the submit RPC needs none either.
 *
 * A member on a stale cached bundle that predates this type does NOT break:
 * `SurveyModal` runs no validation, so an unknown type falls through its
 * ternary to the textarea branch and renders with the right label. Degraded,
 * not broken — and a typed "yes" still submits as the string we want.
 */
export type SurveyChoiceQuestion = {
  id: string;
  type: 'choice';
  label: string;
  options: ['yes', 'no'] | ['yes', 'no', 'maybe'];
  optional?: boolean;
};

export type SurveyQuestion =
  | SurveyScaleQuestion
  | SurveyShortAnswerQuestion
  | SurveyChoiceQuestion;

/** Keyed by question id. Scale answers are integers; short answers and choice
 *  answers are strings. */
export type SurveyAnswers = Record<string, number | string>;

export type SurveyPlacement = 'login' | 'community_enter';
export type SurveyStatus = 'draft' | 'active' | 'closed';

/** Defaults the composer and the member form both rely on. */
export const DEFAULT_SCALE_MAX = 10;
export const DEFAULT_LOW_LABEL = 'Not likely';
export const DEFAULT_HIGH_LABEL = 'Extremely likely';

/**
 * Prefilled into the composer's link fields on a NEW survey. A constant rather
 * than a column default: a DB default would stamp every survey including ones
 * that should not solicit a review, and changing the place id would need a
 * migration. The admin can clear or overwrite either field.
 *
 * The two travel together — the survey schema refuses a URL with no label
 * ("A footer link needs a label"), so defaulting one without the other would
 * fail validation on first save.
 */
export const GOOGLE_REVIEW_URL =
  'https://search.google.com/local/writereview?placeid=ChIJcxs6KqduYU0RSF3RHBmY8JI';
export const GOOGLE_REVIEW_LABEL = 'Leave a Google review';

/** Which end of the member form the optional link renders at. */
export type SurveyFooterLinkPosition = 'top' | 'bottom';

// ─── Member-facing ──────────────────────────────────────────────────────────

/** Exactly what rpc_get_active_survey_for_me returns (camelCase jsonb). */
export interface ActiveSurvey {
  id: string;
  title: string;
  subtitle: string | null;
  questions: SurveyQuestion[];
  isAnonymous: boolean;
  /**
   * Optional link on the form — url is the href, label is the anchor text.
   * Both null when unset. `footer*` is historical: position decides which end
   * it renders at.
   */
  footerLinkUrl: string | null;
  footerLinkLabel: string | null;
  footerLinkPosition: SurveyFooterLinkPosition;
}

/**
 * The one survey due for the caller at this placement, or null.
 *
 * NOTE: this is not a pure read — it CLAIMS the survey by writing the invite
 * row. Calling it twice is safe (the second call sees the invite and returns
 * null) but it must not be used as a speculative "is there anything?" probe,
 * because the first call burns the member's single showing.
 */
export async function fetchActiveSurvey(
  placement: SurveyPlacement,
): Promise<ActiveSurvey | null> {
  const { data, error } = await supabase.rpc('rpc_get_active_survey_for_me', {
    p_placement: placement,
  });
  if (error) throw new Error(error.message);
  return (data as ActiveSurvey | null) ?? null;
}

export interface SubmitSurveyResult {
  ok: boolean;
  /** 0–100 mean of this submission's scale answers; null if it had none. */
  warmth: number | null;
  /** What the server actually did — see the note on `anonymous` below. */
  anonymous: boolean;
}

/**
 * @param anonymous the member ticked "answer anonymously".
 *
 * One-way only. On a survey the admin already marked anonymous the flag is
 * ignored server-side (`is_anonymous OR p_anonymous`), so passing false can
 * never attach a user_id to a response that was promised anonymous. The
 * returned `anonymous` is what actually happened, not what was asked for —
 * render from that, never from the tickbox.
 */
export async function submitSurveyResponse(
  surveyId: string,
  answers: SurveyAnswers,
  anonymous = false,
): Promise<SubmitSurveyResult> {
  const { data, error } = await supabase.rpc('rpc_submit_survey_response', {
    p_survey_id: surveyId,
    p_answers: answers,
    p_anonymous: anonymous,
  });
  if (error) throw new Error(error.message);
  const row = data as { ok?: boolean; warmth?: number | null; anonymous?: boolean } | null;
  return { ok: row?.ok === true, warmth: row?.warmth ?? null, anonymous: row?.anonymous === true };
}

/**
 * "Ask me later" — hide the modal and re-serve after the survey's snooze_duration.
 *
 * Distinct from dismissal, and the distinction is the point: ✕ means "not this,
 * ever" and snooze means "not now". Column-level GRANT UPDATE (snoozed_until)
 * from migration 484 is what makes this a client write at all; everything else
 * on survey_invites is RPC-only.
 *
 * Goes through an RPC rather than writing snoozed_until directly, because the
 * lapse time is `now() + surveys.snooze_duration` and the member payload
 * deliberately does not carry that admin setting. A client stamping plain
 * `now()` would produce an already-lapsed snooze and the modal would come
 * straight back.
 */
export async function snoozeSurveyInvite(surveyId: string): Promise<void> {
  const { error } = await supabase.rpc('rpc_snooze_survey_invite', {
    p_survey_id: surveyId,
  });
  if (error) throw new Error(error.message);
}

/**
 * Stamp dismissal on the caller's own invite. Separate from submission — the
 * member closed the form without answering.
 *
 * `dismissed_at` deliberately does NOT suppress a re-show on its own: the
 * show-once guarantee is the invite row's existence, so a dismissed survey is
 * already never shown again. This records intent for the results view
 * (invited / responded / dismissed) rather than driving behaviour. If dismissal
 * should instead allow N retries, that reads shown_count — no schema change.
 */
export async function dismissSurveyInvite(surveyId: string): Promise<void> {
  const { error } = await supabase
    .from('survey_invites')
    .update({ dismissed_at: new Date().toISOString() })
    .eq('survey_id', surveyId);
  // RLS scopes the UPDATE to the caller's own row (survey_invites_update_own),
  // so no user_id filter is needed here — and adding one would be a lie about
  // where the authorization lives.
  if (error) throw new Error(error.message);
}

// ─── Admin ──────────────────────────────────────────────────────────────────
// snake_case: these come straight off PostgREST, matching admin-mentorship.ts.

/**
 * How many members the survey wants in total. `percent` resolves against the
 * SELECTED AUDIENCE, not the whole system — a percentage of the system is
 * unusable once a filter is set (30% of 375 is 113, against a 3-member
 * community).
 */
export type SurveyTargetMode = 'all' | 'percent' | 'count';

/** Bucket the rate cap counts within. Null rate = uncapped. */
export type SurveyRateUnit = 'day' | 'week';

/** Who gets the slots when a rate cap means not everyone eligible is asked. */
export type SurveySelectionStrategy = 'first_come' | 'random';

export interface SurveyRow {
  id: string;
  name: string;
  title: string;
  subtitle: string | null;
  questions: SurveyQuestion[];
  /**
   * The composer's private brief — what this survey is for. Never reaches a
   * member: rpc_get_active_survey_for_me builds its payload field by field
   * precisely so a column added here cannot leak.
   */
  intent: string | null;
  /** The admin hand-edited the brief, so the composer AI stops rewriting it. */
  intent_pinned: boolean;
  is_anonymous: boolean;
  audience_tier_codes: string[] | null;
  audience_community_ids: string[] | null;
  /** Third audience axis: the RSVPs of one event. NULL = no event restriction. */
  audience_event_id: string | null;
  /** Groups the clones of one recurring survey. Server-owned; only Clone writes it. */
  series_id: string | null;
  /**
   * Legacy proportional filter. Mutually exclusive with target/rate — a DB
   * check constraint forbids the combination, because the two multiply and
   * silently starve the survey.
   */
  sample_every_nth: number | null;
  target_mode: SurveyTargetMode;
  target_value: number | null;
  invite_rate_count: number | null;
  invite_rate_unit: SurveyRateUnit | null;
  selection_strategy: SurveySelectionStrategy;
  auto_close_on_target: boolean;
  /** Set when the target is reached. Sampling stops; the survey stays active so
   *  members holding an open invite can still submit. */
  sampling_closed_at: string | null;
  max_shows: number;
  footer_link_url: string | null;
  footer_link_label: string | null;
  footer_link_position: SurveyFooterLinkPosition;
  placement: SurveyPlacement;
  starts_at: string | null;
  ends_at: string | null;
  status: SurveyStatus;
  created_at: string;
  updated_at: string;
}

export interface SurveyResponseRow {
  id: string;
  survey_id: string;
  /** NULL on an anonymous survey — by design, not a data gap. */
  user_id: string | null;
  tier_code: string | null;
  answers: SurveyAnswers;
  warmth: number | null;
  submitted_at: string;
}

/** Draft payload for create/update. Server owns created_by and timestamps. */
export interface SurveyInput {
  name: string;
  title: string;
  subtitle?: string | null;
  questions: SurveyQuestion[];
  intent?: string | null;
  intent_pinned?: boolean;
  is_anonymous: boolean;
  audience_tier_codes: string[] | null;
  sample_every_nth: number | null;
  target_mode: SurveyTargetMode;
  target_value: number | null;
  invite_rate_count: number | null;
  invite_rate_unit: SurveyRateUnit | null;
  selection_strategy: SurveySelectionStrategy;
  auto_close_on_target: boolean;
  /**
   * Optional because no composer control writes it yet — the community selector
   * lands in a later phase. Omitting the key leaves the stored value alone;
   * sending null would clear it, so it stays absent rather than a placeholder.
   */
  audience_community_ids?: string[] | null;
  /**
   * Optional like the community axis: absent leaves the stored value alone,
   * explicit null clears it. `series_id` is deliberately NOT here — it is
   * server-owned and only rpc_clone_survey writes it.
   */
  audience_event_id?: string | null;
  footer_link_url?: string | null;
  footer_link_label?: string | null;
  footer_link_position?: SurveyFooterLinkPosition;
  placement: SurveyPlacement;
  starts_at: string | null;
  ends_at: string | null;
  status: SurveyStatus;
}

export async function fetchSurveys(): Promise<SurveyRow[]> {
  const { data, error } = await supabase
    .from('surveys')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as SurveyRow[];
}

export async function createSurvey(input: SurveyInput): Promise<SurveyRow> {
  const { data, error } = await supabase.from('surveys').insert(input).select('*').single();
  if (error) throw new Error(error.message);
  return data as SurveyRow;
}

export async function updateSurvey(id: string, input: Partial<SurveyInput>): Promise<SurveyRow> {
  const { data, error } = await supabase
    .from('surveys')
    .update(input)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as SurveyRow;
}

export async function fetchSurveyResponses(surveyId: string): Promise<SurveyResponseRow[]> {
  const { data, error } = await supabase
    .from('survey_responses')
    .select('*')
    .eq('survey_id', surveyId)
    .order('submitted_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as SurveyResponseRow[]).map((r) => ({
    ...r,
    // real/numeric can arrive as a string over PostgREST.
    warmth: r.warmth != null ? Number(r.warmth) : null,
  }));
}

/** Invite-level counts: how many were asked vs how many answered. */
export interface SurveyReach {
  invited: number;
  responded: number;
  dismissed: number;
}

export async function fetchSurveyReach(surveyId: string): Promise<SurveyReach> {
  const { data, error } = await supabase
    .from('survey_invites')
    .select('responded_at, dismissed_at')
    .eq('survey_id', surveyId);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as { responded_at: string | null; dismissed_at: string | null }[];
  return {
    invited: rows.length,
    responded: rows.filter((r) => r.responded_at != null).length,
    dismissed: rows.filter((r) => r.dismissed_at != null && r.responded_at == null).length,
  };
}

export interface SurveyRespondent {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  responded_at: string | null;
  dismissed_at: string | null;
}

/**
 * Everyone invited to a survey, newest responder first.
 *
 * Two queries rather than a PostgREST embed: `survey_invites.user_id` references
 * `auth.users`, not `user_profiles`, so there is no FK for PostgREST to embed
 * through. Names and avatars come from user_profiles in a second round trip.
 *
 * EMAIL IS NOT HERE. It lives in auth.users, which authenticated clients cannot
 * read — surfacing it needs a super_admin SECURITY DEFINER RPC (follow-up
 * migration). Name + avatar is what this can honestly return today.
 */
export async function fetchSurveyRespondents(surveyId: string): Promise<SurveyRespondent[]> {
  const { data: invites, error } = await supabase
    .from('survey_invites')
    .select('user_id, responded_at, dismissed_at')
    .eq('survey_id', surveyId)
    .order('responded_at', { ascending: false, nullsFirst: false });
  if (error) throw new Error(error.message);

  const rows = (invites ?? []) as Omit<SurveyRespondent, 'full_name' | 'avatar_url'>[];
  if (rows.length === 0) return [];

  const { data: profiles, error: pErr } = await supabase
    .from('user_profiles')
    .select('id, full_name, avatar_url')
    .in('id', rows.map((r) => r.user_id));
  if (pErr) throw new Error(pErr.message);

  const byId = new Map(
    ((profiles ?? []) as { id: string; full_name: string | null; avatar_url: string | null }[]).map(
      (p) => [p.id, p],
    ),
  );

  return rows.map((r) => ({
    ...r,
    full_name: byId.get(r.user_id)?.full_name ?? null,
    avatar_url: byId.get(r.user_id)?.avatar_url ?? null,
  }));
}

// ─── Audience ───────────────────────────────────────────────────────────────

export interface CommunityOption {
  id: string;
  name: string;
  /** Non-null = archived. Kept selectable-if-already-selected, never offerable. */
  archived_at: string | null;
}

/**
 * Every community, archived included.
 *
 * Archived ones are returned deliberately: a survey saved last month may target
 * a community that has since been archived, and dropping it from the list would
 * make the composer silently discard that filter on the next save. The UI shows
 * it disabled instead. `communities_select` lets a super_admin read all rows;
 * a non-admin gets only their own, which is fine — this is an admin surface.
 */
export async function fetchCommunities(): Promise<CommunityOption[]> {
  const { data, error } = await supabase
    .from('communities')
    .select('id, name, archived_at')
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as CommunityOption[];
}

/**
 * How many members match the selected filters, right now.
 *
 * Tiers OR within their axis, communities OR within theirs, and the two axes
 * AND together — the same rule rpc_get_active_survey_for_me applies, evaluated
 * by the same function, so the readout cannot drift from the behaviour.
 * Null filters mean "no restriction on this axis".
 */
export async function fetchAudienceSize(
  tierCodes: string[] | null,
  communityIds: string[] | null,
  eventId: string | null = null,
): Promise<number> {
  const { data, error } = await supabase.rpc('rpc_survey_audience_size', {
    p_tier_codes: tierCodes?.length ? tierCodes : null,
    p_community_ids: communityIds?.length ? communityIds : null,
    p_event_id: eventId || null,
  });
  if (error) throw new Error(error.message);
  return Number(data ?? 0);
}

/**
 * Delete a survey and everything filed under it.
 *
 * No RPC and no migration: `surveys_admin_all` already grants super_admins ALL
 * on the table, and survey_responses, survey_invites and survey_intent_messages
 * all carry `ON DELETE CASCADE`. One DELETE removes the lot.
 *
 * It is irreversible, and the answers are the part that cannot be reconstructed
 * — an anonymous response has no link back to a person, so there is nothing to
 * re-derive it from. The caller must confirm first and must say how many
 * answers are going (see fetchSurveyResponseCount).
 */
export async function deleteSurvey(surveyId: string): Promise<void> {
  const { error } = await supabase.from('surveys').delete().eq('id', surveyId);
  if (error) throw new Error(error.message);
}

/** How many answers a delete would destroy. Shown in the confirmation. */
export async function fetchSurveyResponseCount(surveyId: string): Promise<number> {
  const { count, error } = await supabase
    .from('survey_responses')
    .select('*', { count: 'exact', head: true })
    .eq('survey_id', surveyId);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** One potential recipient, as the composer's audience browser lists them. */
export interface AudienceMember {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  /** NULL when the member holds no ACTIVE community membership. */
  tier_code: string | null;
}

/** Thrown when the roster is withheld to protect anonymous answers. */
export const ROSTER_WITHHELD = 'ROSTER_WITHHELD' as const;

/**
 * The people the current audience settings resolve to.
 *
 * The empty-array-to-null normalisation MUST match fetchAudienceSize above,
 * character for character. `NULL` means "this axis is off"; `{}` means "match
 * nothing", because `= ANY('{}')` is never true. Get the two out of step and
 * the count and the roster describe different sets while both look successful.
 *
 * Members with no name set are excluded server-side (5 of 448 on prod), so this
 * array can be SHORTER than fetchAudienceSize. That gap is shown in the dialog
 * header, never hidden.
 *
 * surveyId is what lets the server protect anonymity: it refuses once an
 * anonymous survey has responses. Pass null for an unsaved draft.
 */
export async function fetchAudienceMembers(
  tierCodes: string[] | null,
  communityIds: string[] | null,
  eventId: string | null = null,
  surveyId: string | null = null,
): Promise<AudienceMember[]> {
  const { data, error } = await supabase.rpc('rpc_survey_audience_members', {
    p_tier_codes: tierCodes?.length ? tierCodes : null,
    p_community_ids: communityIds?.length ? communityIds : null,
    p_event_id: eventId || null,
    p_survey_id: surveyId || null,
  });
  if (error) {
    // P0001 carries the anonymity refusal. Re-thrown as a stable token so the
    // dialog can explain it without matching on Postgres message text.
    if (error.message?.includes('roster withheld')) throw new Error(ROSTER_WITHHELD);
    throw new Error(error.message);
  }
  return (data ?? []) as AudienceMember[];
}

/** An event the composer can aim a survey at, with how many people RSVPd. */
export interface RsvpEventOption {
  id: string;
  title: string;
  start_at: string;
  rsvp_count: number;
}

/**
 * Events a survey can be aimed at: anything with at least one 'registered'
 * RSVP, from the last 12 months and anything upcoming (Tim, 30 Sep).
 *
 * Counts 'registered' only, matching survey_audience_size and the eligibility
 * filter — so the number beside an event in the picker is the number the server
 * will actually invite. 'attended' is excluded because it has never been
 * written in prod; declined and cancelled because those people were not there.
 */
export async function fetchRsvpEvents(): Promise<RsvpEventOption[]> {
  const since = new Date();
  since.setMonth(since.getMonth() - 12);

  const { data, error } = await supabase
    .from('community_events')
    .select('id, title, start_at, community_event_registrations(status)')
    .gte('start_at', since.toISOString())
    .order('start_at', { ascending: false });
  if (error) throw new Error(error.message);

  return ((data ?? []) as unknown as Array<{
    id: string;
    title: string;
    start_at: string;
    community_event_registrations: Array<{ status: string }> | null;
  }>)
    .map((e) => ({
      id: e.id,
      title: e.title,
      start_at: e.start_at,
      rsvp_count: (e.community_event_registrations ?? [])
        .filter((r) => r.status === 'registered').length,
    }))
    // An event nobody registered for would resolve to an empty audience, so
    // offering it is offering a survey that can never be shown.
    .filter((e) => e.rsvp_count > 0);
}

/**
 * Clone a survey into a new draft. Returns the new survey's id.
 *
 * Server-side deliberately: `SurveyInput` carries no rest_period,
 * snooze_duration or max_shows, so a client-side read-then-createSurvey would
 * silently reset all three to DB defaults — and a 6-month rest floor appearing
 * on a weekly survey looks exactly like "the survey stopped working".
 *
 * The clone keeps the question ids, so weeks of the same series compare.
 */
export async function cloneSurvey(surveyId: string): Promise<string> {
  const { data, error } = await supabase.rpc('rpc_clone_survey', {
    p_survey_id: surveyId,
  });
  if (error) throw new Error(error.message);
  return String(data);
}

// ─── Composer AI ────────────────────────────────────────────────────────────

/**
 * A drafted question plus the note explaining it.
 *
 * `rationale` is admin-facing only and is stripped before the set is saved —
 * rpc_get_active_survey_for_me hands `surveys.questions` to the member
 * wholesale, so anything left on the object ships to them. The analytics
 * context reads rationales back from the composition thread instead.
 */
export type ComposedQuestion = SurveyQuestion & { rationale?: string };

/** Strip the admin-only fields before a set is written to surveys.questions. */
export function stripRationales(questions: ComposedQuestion[]): SurveyQuestion[] {
  return questions.map(({ rationale: _rationale, ...q }) => q as SurveyQuestion);
}

export interface IntentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  draft_questions: ComposedQuestion[] | null;
  summary: string | null;
  created_at: string;
}

/**
 * The composition thread, oldest first, plus the brief as it currently stands.
 *
 * The brief is the last non-null `summary` rather than a field of its own,
 * because before the survey row exists there is nowhere else for it to live.
 * Once the thread is attached, `surveys.intent` is authoritative and the caller
 * prefers that — see SurveyIntentPanel.
 */
export async function fetchIntentThread(
  threadKey: string,
): Promise<{ messages: IntentMessage[]; summary: string | null }> {
  const { data, error } = await supabase
    .from('survey_intent_messages')
    .select('id, role, content, draft_questions, summary, created_at')
    .eq('thread_key', threadKey)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);

  const messages = (data ?? []) as IntentMessage[];
  const latest = [...messages].reverse().find((m) => m.summary != null);
  return { messages, summary: latest?.summary ?? null };
}

/**
 * The thread key behind an existing survey, or null if it was composed by hand.
 *
 * Threads are keyed by thread_key rather than survey_id because they start
 * before the survey row exists; survey_id is backfilled on save. Reopening a
 * saved survey therefore has to look the key back up.
 */
export async function fetchThreadKeyForSurvey(surveyId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('survey_intent_messages')
    .select('thread_key')
    .eq('survey_id', surveyId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as { thread_key: string } | null)?.thread_key ?? null;
}

interface ComposeChatArgs {
  threadKey: string;
  surveyId?: string | null;
  message: string;
  currentQuestions?: ComposedQuestion[] | null;
  /** Send ONLY when the admin has edited the brief box. See `pinned`. */
  summary?: string | null;
  /**
   * True pins the brief: the model reads it but stops rewriting it. Sending a
   * `summary` does NOT imply this — a panel that echoed its textarea on every
   * turn would otherwise freeze the brief on the first message, silently.
   */
  pinned?: boolean;
}

export interface ComposeChatResult {
  prose: string;
  questions: ComposedQuestion[] | null;
  intent: string | null;
}

/**
 * Dig the real message out of a functions.invoke failure.
 *
 * supabase-js reports EVERY non-2xx as the same string — "Edge Function
 * returned a non-2xx status code" — and hides the response on `error.context`.
 * Without unwrapping it, "the question generator is unavailable" (502) and
 * "super admin access required" (403) are indistinguishable to the panel, and
 * the model-unavailable state it renders can never actually be reached.
 */
async function readFunctionError(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: unknown }).context;
  if (!(ctx instanceof Response)) return null;
  try {
    const body = (await ctx.clone().json()) as { error?: unknown };
    return typeof body?.error === 'string' && body.error ? body.error : null;
  } catch {
    // A non-JSON body (a gateway HTML error page, say) is not more informative
    // than the generic message — fall back rather than surfacing markup.
    return null;
  }
}

async function invokeCompose<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('survey-compose', {
    region: EDGE_FUNCTION_INVOKE_REGION,
    body,
  });
  if (!error) return data as T;
  throw new Error((await readFunctionError(error)) ?? error.message);
}

export function composeChat(args: ComposeChatArgs): Promise<ComposeChatResult> {
  return invokeCompose<ComposeChatResult>({ action: 'chat', ...args });
}

/**
 * Replace one question in a set.
 *
 * The whole set goes up, not just the target: a question is not independent of
 * its neighbours — it must not duplicate one, it has to keep the scale
 * direction, and if it is half of the corroboration pair the replacement has to
 * still corroborate.
 */
export function composeRegenerate(args: {
  threadKey: string;
  surveyId?: string | null;
  questions: ComposedQuestion[];
  index: number;
  reason?: string | null;
}): Promise<{ question: ComposedQuestion }> {
  return invokeCompose<{ question: ComposedQuestion }>({ action: 'regenerate', ...args });
}

/** Backfill survey_id onto the thread once the survey row exists. */
export function composeAttach(
  threadKey: string,
  surveyId: string,
): Promise<{ attached: number }> {
  return invokeCompose<{ attached: number }>({ action: 'attach', threadKey, surveyId });
}

// ─── Warmth presentation ────────────────────────────────────────────────────

/**
 * Bucket a 0–100 warmth score into a semantic token band.
 *
 * Semantic tokens ONLY (success / warning / danger). Not the FutureBuilder
 * accents — fbLime/fbOrange are celebration-only, and a sentiment readout is a
 * resting state. Both classes resolve per-theme, so light/dark parity is free.
 */
export type WarmthBand = 'positive' | 'mixed' | 'negative' | 'none';

export function warmthBand(warmth: number | null | undefined): WarmthBand {
  if (warmth == null || Number.isNaN(warmth)) return 'none';
  if (warmth >= 67) return 'positive';
  if (warmth >= 34) return 'mixed';
  return 'negative';
}

export const WARMTH_BAND_CLASS: Record<WarmthBand, string> = {
  positive: 'text-success bg-success/10 border-success/20',
  mixed: 'text-warning bg-warning/10 border-warning/20',
  negative: 'text-danger bg-danger/10 border-danger/20',
  none: 'text-muted-foreground bg-muted/50 border-border',
};

export const WARMTH_BAND_LABEL: Record<WarmthBand, string> = {
  positive: 'Positive',
  mixed: 'Mixed',
  negative: 'Negative',
  none: 'No score',
};

/** Mean warmth across responses that have one. Null when none do. */
export function averageWarmth(rows: Pick<SurveyResponseRow, 'warmth'>[]): number | null {
  const scored = rows.map((r) => r.warmth).filter((w): w is number => w != null);
  if (scored.length === 0) return null;
  return scored.reduce((a, b) => a + b, 0) / scored.length;
}
